import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { problemDetail } from '@up2d8/api-client';
import type {
  AllowedDomainList,
  AudienceSpec,
  Shortcut,
  ShortcutColor,
  ShortcutIcon,
  ShortcutKind,
  ShortcutList,
} from '@up2d8/contracts';
import {
  btnClass,
  ActionsCell,
  AdminIcon,
  AdminPage,
  Btn,
  Card,
  CardHead,
  CardPad,
  Drawer,
  Empty,
  Field,
  IconBtn,
  Input,
  Note,
  PageHead,
  Segmented,
  SwitchRow,
  Table,
  useAdminToast,
} from '../admin-kit';
import { AudienceField } from '../beheer-communicatie/audience-field';
import { LoadError, Loading, usePageError } from '../beheer-communicatie/page-state';
import { useSession } from '../../shared/session';
import { useUpload } from '../../shared/use-upload';
import { audienceIncomplete } from '../../shared/audience-preview';

/**
 * Tegels beheren (Stap 19, nieuw ontwerp in BHR-5, frames C7 en C8).
 *
 * Links de tegels in de volgorde van de app, rechts de toegelaten domeinen: zonder toegelaten
 * domein kan er geen tegel bestaan. Een nieuwe tegel en een tegel wijzigen gebeuren in hetzelfde
 * paneel (C8), zodat de lijst - en de tellers die zeggen of een tegel gebruikt wordt - zichtbaar
 * blijft.
 */

const ICON_OPTIONS: ShortcutIcon[] = [
  'payslip',
  'calendar',
  'clock',
  'document',
  'euro',
  'health',
  'link',
  'tools',
];

/**
 * De acht kleuren als volle tegel met een wit icoon, in de volgorde van de mockup (C8).
 *
 * De kleur blijft een rol en geen hexcode (zie het contract): in de app wordt ze een zachte tint
 * met een donker icoon (`app-shortcuts.tsx`). Hier staat per rol de mockupkleur die er het best bij
 * past; alle acht halen minstens 4,5:1 met wit. Paars uit de mockup heeft geen rol en staat er
 * niet; "warning" (geel) kreeg een donker okergeel (`#A16207`, 4,9:1), zie deviations.md.
 *
 * De drie merkkleuren staan als token: met een eigen huisstijl volgen ze die (`shared/theme.ts`).
 */
const SWATCH: Record<ShortcutColor, string> = {
  primary: 'var(--a-primary)',
  dark: 'var(--brand-dark-blue)',
  orange: 'var(--a-orange)',
  success: '#15803D',
  danger: '#B91C1C',
  warning: '#A16207',
  cyan: '#0F766E',
  neutral: '#4B5563',
};
const COLOR_OPTIONS = Object.keys(SWATCH) as ShortcutColor[];

/** Het icoon van een tegel, met de paden uit de mockup. */
function ShortcutGlyph({ icon, size = 16 }: { icon: ShortcutIcon; size?: number }) {
  switch (icon) {
    case 'payslip':
      return <AdminIcon name="fileText" size={size} />;
    case 'calendar':
      return <AdminIcon name="calendar" size={size} />;
    case 'clock':
      return <AdminIcon name="clock" size={size} />;
    case 'document':
      return <AdminIcon name="file" size={size} />;
    case 'euro':
      return <AdminIcon name="chart" size={size} />;
    case 'health':
      return <AdminIcon name="alert" size={size} />;
    case 'link':
      return <AdminIcon name="link" size={size} />;
    case 'tools':
      return <AdminIcon name="wrench" size={size} />;
  }
}

export function ShortcutsAdminPage() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const queryClient = useQueryClient();
  const toast = useAdminToast();
  const pageError = usePageError();
  const [drawer, setDrawer] = useState<{ shortcut: Shortcut | null } | null>(null);
  const [sleept, setSleept] = useState<string | null>(null);

  const shortcuts = useQuery({
    queryKey: ['shortcuts'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/shortcuts');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ShortcutList).items;
    },
  });

  const remove = useMutation({
    mutationFn: async (shortcutId: string) => {
      const { error } = await api.DELETE('/api/v1/shortcuts/{shortcutId}', {
        params: { path: { shortcutId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      pageError.clear();
      void queryClient.invalidateQueries({ queryKey: ['shortcuts'] });
      toast.show(t('beheer.communicatie.shortcuts.deleted'));
    },
    onError: (error) => pageError.show(problemDetail(error) ?? t('errors.generic')),
  });

  /** De volgorde vastleggen (Extra 6, punt 10). De volledige lijst, zoals hij op het scherm staat. */
  const reorder = useMutation({
    mutationFn: async (shortcutIds: string[]) => {
      const { error } = await api.POST('/api/v1/shortcuts/reorder', { body: { shortcutIds } });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shortcuts'] });
    },
    onError: (error) => pageError.show(problemDetail(error) ?? t('errors.generic')),
  });

  const items = shortcuts.data ?? [];

  /** Eén tegel naar een andere plek; de rest schuift mee. */
  const verplaatsNaar = (shortcutId: string, doel: number): void => {
    const huidig = items.map((item) => item.id);
    const van = huidig.indexOf(shortcutId);
    if (van === -1 || doel < 0 || doel >= huidig.length) return;

    const volgorde = [...huidig];
    volgorde.splice(van, 1);
    volgorde.splice(doel, 0, shortcutId);
    reorder.mutate(volgorde);
  };

  return (
    <AdminPage>
      <PageHead
        title={t('shortcutsAdmin.title')}
        sub={t('beheer.communicatie.shortcuts.sub')}
        actions={
          <Btn
            icon={<AdminIcon name="plus" />}
            testId="shortcut-new"
            onClick={() => setDrawer({ shortcut: null })}
          >
            {t('beheer.communicatie.shortcuts.new')}
          </Btn>
        }
      />

      {pageError.element}

      <div className="a-grid side">
        <Card>
          <CardHead
            title={t('shortcutsAdmin.listTitle')}
            count={shortcuts.isSuccess ? items.length : undefined}
          />
          {shortcuts.isPending ? (
            <Loading />
          ) : shortcuts.isError ? (
            <LoadError onRetry={() => void shortcuts.refetch()} error={shortcuts.error} />
          ) : items.length === 0 ? (
            <Empty
              icon={<AdminIcon name="link" size={24} />}
              title={t('shortcutsAdmin.emptyTitle')}
              text={t('beheer.communicatie.shortcuts.emptyHint')}
            />
          ) : (
            <Table label={t('shortcutsAdmin.listTitle')} testId="shortcut-admin-list">
              <thead>
                <tr>
                  <th>{t('beheer.communicatie.shortcuts.colTile')}</th>
                  <th>{t('beheer.communicatie.colAudience')}</th>
                  <th>{t('beheer.communicatie.shortcuts.colOpened')}</th>
                  <th className="act">
                    <span className="a-sr">{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((shortcut, index) => (
                  <tr
                    key={shortcut.id}
                    /*
                      Slepen met de muis, pijltjes voor de rest (Extra 6, punt 10).

                      `draggable` alleen zou de volgorde onbereikbaar maken voor wie met een
                      toetsenbord werkt of een schermlezer gebruikt - en voor iedereen op een
                      telefoon, waar een HTML5-sleep niet bestaat. Vandaar twee wegen naar dezelfde
                      handeling; de knoppen zijn de echte, het slepen is het gemak.
                    */
                    draggable
                    onDragStart={() => setSleept(shortcut.id)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (sleept && sleept !== shortcut.id) verplaatsNaar(sleept, index);
                      setSleept(null);
                    }}
                    data-testid="shortcut-admin-row"
                  >
                    <td>
                      <div className="a-person">
                        <span
                          className="a-tile c-solid"
                          style={{ background: SWATCH[shortcut.color] }}
                          aria-hidden="true"
                        >
                          {shortcut.iconUrl ? (
                            <img
                              src={shortcut.iconUrl}
                              alt=""
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                background: 'white',
                                borderRadius: 'inherit',
                                padding: 2,
                              }}
                            />
                          ) : (
                            <ShortcutGlyph icon={shortcut.icon} size={20} />
                          )}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div className="a-name">{shortcut.label}</div>
                          <div className="a-meta a-mono" style={{ overflowWrap: 'anywhere' }}>
                            {shortcut.urlTemplate}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{shortcut.audienceDescription}</td>
                    <td>
                      {/* Aantallen, nooit namen: wie waarop tikt, is geen bedrijfsbelang (ADR-0014). */}
                      <span className="a-muted">
                        {t('beheer.communicatie.shortcuts.opened', {
                          clicks: shortcut.clickCount,
                          count: shortcut.uniqueClickers,
                        })}
                      </span>
                    </td>
                    <ActionsCell>
                      <IconBtn
                        label={t('shortcutsAdmin.moveUp', { label: shortcut.label })}
                        icon={<AdminIcon name="arrowUp" />}
                        disabled={index === 0 || reorder.isPending}
                        testId="shortcut-move-up"
                        onClick={() => verplaatsNaar(shortcut.id, index - 1)}
                      />
                      <IconBtn
                        label={t('shortcutsAdmin.moveDown', { label: shortcut.label })}
                        icon={<AdminIcon name="arrowDown" />}
                        disabled={index === items.length - 1 || reorder.isPending}
                        testId="shortcut-move-down"
                        onClick={() => verplaatsNaar(shortcut.id, index + 1)}
                      />
                      <IconBtn
                        label={t('beheer.communicatie.shortcuts.edit', { label: shortcut.label })}
                        icon={<AdminIcon name="pencil" />}
                        testId="shortcut-edit"
                        onClick={() => setDrawer({ shortcut })}
                      />
                      <IconBtn
                        label={t('beheer.communicatie.shortcuts.delete', { label: shortcut.label })}
                        icon={<AdminIcon name="trash" />}
                        disabled={remove.isPending}
                        testId="shortcut-delete"
                        onClick={() => remove.mutate(shortcut.id)}
                      />
                    </ActionsCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {has('shortcuts.domain.manage') ? (
          <DomainCard />
        ) : (
          <Card>
            <CardHead title={t('beheer.communicatie.shortcuts.domainsTitle')} />
            <CardPad>
              <p className="a-small a-muted">{t('shortcutsAdmin.domainsNoRight')}</p>
            </CardPad>
          </Card>
        )}
      </div>

      {drawer ? (
        <ShortcutDrawer
          key={drawer.shortcut?.id ?? 'new'}
          shortcut={drawer.shortcut}
          onClose={() => setDrawer(null)}
          onDone={(message) => {
            setDrawer(null);
            pageError.clear();
            toast.show(message);
          }}
        />
      ) : null}

      {toast.element}
    </AdminPage>
  );
}

/**
 * De toegelaten domeinen (C7, rechts).
 *
 * Met een waarschuwing erbij, want dit is de enige echt gevaarlijke knop van de module: een domein
 * toestaan betekent dat de server medewerkers daarnaartoe mag leiden, met hun personeelsnummer in
 * de URL.
 */
function DomainCard() {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const domains = useQuery({
    queryKey: ['allowed-domains'],
    queryFn: async () => {
      const { data, error: failed } = await api.GET('/api/v1/shortcuts/domains/allowed');
      if (failed || !data) throw failed ?? new Error('Ophalen mislukt');
      return (data as AllowedDomainList).items;
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const { error: failed } = await api.POST('/api/v1/shortcuts/domains/allowed', {
        body: { domain },
      });
      if (failed) throw failed;
    },
    onSuccess: () => {
      setDomain('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['allowed-domains'] });
    },
    onError: (failed) => setError(problemDetail(failed) ?? t('shortcutsAdmin.domainFailed')),
  });

  const remove = useMutation({
    mutationFn: async (domainId: string) => {
      const { error: failed } = await api.DELETE('/api/v1/shortcuts/domains/allowed/{domainId}', {
        params: { path: { domainId } },
      });
      if (failed) throw failed;
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['allowed-domains'] });
      void queryClient.invalidateQueries({ queryKey: ['shortcuts'] });
    },
    onError: (failed) => setError(problemDetail(failed) ?? t('errors.generic')),
  });

  const items = domains.data ?? [];

  return (
    <Card testId="domain-card">
      <CardHead
        title={t('beheer.communicatie.shortcuts.domainsTitle')}
        count={domains.isSuccess ? items.length : undefined}
      />
      <CardPad>
        <div className="a-hint">{t('beheer.communicatie.shortcuts.domainsHint')}</div>
        <Note tone="attention">{t('shortcutsAdmin.domainsWarning')}</Note>

        {domains.isPending ? (
          <div className="a-muted a-small" role="status">
            {t('common.loading')}
          </div>
        ) : domains.isError ? (
          <Note tone="danger" role="alert">
            {t('beheer.communicatie.loadFailed')}
          </Note>
        ) : items.length === 0 ? (
          <p className="a-small a-muted">{t('shortcutsAdmin.domainsEmpty')}</p>
        ) : (
          <div className="a-actions" data-testid="domain-list">
            {items.map((entry) => (
              <span key={entry.id} className="a-chip a-mono">
                {entry.domain}
                <button
                  type="button"
                  aria-label={t('shortcutsAdmin.domainRemove', { domain: entry.domain })}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(entry.id)}
                >
                  <AdminIcon name="x" size={14} />
                </button>
              </span>
            ))}
          </div>
        )}

        <form
          className="a-inline"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (domain.trim().length > 0 && !add.isPending) add.mutate();
          }}
        >
          <Input
            id="allowed-domain"
            aria-label={t('shortcutsAdmin.domainLabel')}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'allowed-domain-error' : undefined}
            placeholder={t('beheer.communicatie.shortcuts.domainPlaceholder')}
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
          />
          <Btn
            type="submit"
            variant="sec"
            size="sm"
            disabled={domain.trim().length === 0 || add.isPending}
          >
            {t('shortcutsAdmin.domainAdd')}
          </Btn>
        </form>
        {error ? (
          <div className="a-error" id="allowed-domain-error" role="alert">
            <AdminIcon name="alert" size={16} />
            <span>{error}</span>
          </div>
        ) : null}
      </CardPad>
    </Card>
  );
}

/**
 * Een nieuwe tegel of een bestaande wijzigen (C8).
 *
 * Wijzigen bestaat sinds UI-5: een tegel kon tot dan alleen aangemaakt en verwijderd worden. Een
 * kleur of een typefout herstellen betekende opnieuw beginnen, en dan begonnen de tellers op nul -
 * de enige cijfers die zeggen of een tegel gebruikt wordt.
 *
 * Bij wijzigen gaat alleen mee wat veranderde. Dat is geen zuinigheid: de server herberekent de
 * doelgroepomschrijving alleen als de doelgroep zelf meekomt, en dat is een aanroep naar een andere
 * module die een beheerder met een typefout niet hoeft te betalen.
 */
function ShortcutDrawer({
  shortcut,
  onClose,
  onDone,
}: {
  shortcut: Shortcut | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState(shortcut?.label ?? '');
  const [icon, setIcon] = useState<ShortcutIcon>(shortcut?.icon ?? 'payslip');
  // Een eigen icoon (FBK-8): het bestand en een voorbeeld. `null` is het bibliotheekicoon.
  const [iconFileId, setIconFileId] = useState<string | null>(shortcut?.iconFileId ?? null);
  const [iconPreview, setIconPreview] = useState<string | null>(shortcut?.iconUrl ?? null);
  const upload = useUpload();
  const [color, setColor] = useState<ShortcutColor>(shortcut?.color ?? 'primary');
  const [kind, setKind] = useState<ShortcutKind>(shortcut?.kind ?? 'fixed');
  const [urlTemplate, setUrlTemplate] = useState(shortcut?.urlTemplate ?? '');
  const [audience, setAudience] = useState<AudienceSpec>(shortcut?.audience ?? { type: 'all' });
  const [showOnHome, setShowOnHome] = useState(shortcut?.showOnHome ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!shortcut) {
        const { error: failed } = await api.POST('/api/v1/shortcuts', {
          body: {
            label,
            icon,
            iconFileId,
            color,
            kind,
            urlTemplate,
            showOnHome,
            audience,
          } as never,
        });
        if (failed) throw failed;
        return;
      }

      // Alleen de velden die werkelijk veranderden; `PATCH` laat de rest staan.
      const body: Record<string, unknown> = {};
      if (label !== shortcut.label) body.label = label;
      if (icon !== shortcut.icon) body.icon = icon;
      if (iconFileId !== (shortcut.iconFileId ?? null)) body.iconFileId = iconFileId;
      if (color !== shortcut.color) body.color = color;
      if (kind !== shortcut.kind) body.kind = kind;
      if (urlTemplate !== shortcut.urlTemplate) body.urlTemplate = urlTemplate;
      if (showOnHome !== shortcut.showOnHome) body.showOnHome = showOnHome;
      if (JSON.stringify(audience) !== JSON.stringify(shortcut.audience)) body.audience = audience;

      if (Object.keys(body).length === 0) return;

      const { error: failed } = await api.PATCH('/api/v1/shortcuts/{shortcutId}', {
        params: { path: { shortcutId: shortcut.id } },
        body: body as never,
      });
      if (failed) throw failed;
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['shortcuts'] });
      onDone(shortcut ? t('shortcutsAdmin.saved') : t('shortcutsAdmin.created'));
    },
    onError: (failed: unknown) => {
      // De server legt uit wat er mis is met de URL (domein niet toegestaan, geen https, veld
      // bestaat niet). Die uitleg is nuttiger dan een algemene melding.
      setError(
        problemDetail(failed) ??
          (shortcut ? t('shortcutsAdmin.saveFailed') : t('shortcutsAdmin.failed')),
      );
    },
  });

  // Ook geen lege doelgroep: "Kiezen" zonder keuze weigert de server (hertest 30 okt.).
  const complete =
    label.trim().length > 0 && urlTemplate.trim().length > 0 && !audienceIncomplete(audience);

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        shortcut
          ? t('beheer.communicatie.shortcuts.editTitle')
          : t('beheer.communicatie.shortcuts.new')
      }
      sub={shortcut?.label}
      testId="shortcut-drawer"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            type="submit"
            form="shortcut-form"
            disabled={!complete || save.isPending}
            testId={shortcut ? 'shortcut-edit-save' : 'shortcut-save'}
          >
            {t('beheer.communicatie.shortcuts.save')}
          </Btn>
        </>
      }
    >
      <form
        id="shortcut-form"
        className="a-form"
        noValidate
        data-testid={shortcut ? 'shortcut-edit-form' : 'shortcut-form'}
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !save.isPending) save.mutate();
        }}
      >
        <Field
          label={t('shortcutsAdmin.labelField')}
          hint={t('beheer.communicatie.shortcuts.labelHint')}
        >
          {(props) => (
            <Input
              {...props}
              value={label}
              maxLength={60}
              placeholder={t('shortcutsAdmin.labelPlaceholder')}
              onChange={(event) => setLabel(event.target.value)}
            />
          )}
        </Field>

        <div className="a-field">
          <div className="a-label" id="shortcut-icon-label">
            {t('shortcutsAdmin.icon')}
          </div>
          <div className="a-actions" role="radiogroup" aria-labelledby="shortcut-icon-label">
            {ICON_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={icon === option}
                aria-label={t(`shortcuts.icon.${option}`)}
                title={t(`shortcuts.icon.${option}`)}
                className="a-iconbtn c-pick"
                onClick={() => {
                  setIcon(option);
                  setIconFileId(null);
                  setIconPreview(null);
                }}
              >
                <ShortcutGlyph icon={option} />
              </button>
            ))}
          </div>
          {/* Een eigen icoon (FBK-8): PNG, JPEG of WebP, vierkant op een witte tegel getoond. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            {iconPreview ? (
              <img
                src={iconPreview}
                alt=""
                data-testid="shortcut-icon-preview"
                style={{
                  width: 44,
                  height: 44,
                  objectFit: 'contain',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'white',
                  padding: 2,
                }}
              />
            ) : null}
            <label
              className={btnClass({ variant: 'sec', size: 'sm' })}
              style={{ cursor: 'pointer' }}
            >
              <AdminIcon name="upload" size={16} />
              {iconFileId ? t('shortcutIcon.replace') : t('shortcutIcon.upload')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="shortcut-icon-upload"
                style={{ display: 'none' }}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  if (file.size > 512 * 1024) {
                    setError(t('shortcutIcon.tooLarge'));
                    return;
                  }
                  const fileId = await upload.upload(file, {
                    ownerModule: 'shortcuts',
                    purpose: 'shortcut_icon',
                  });
                  if (!fileId) return;
                  setIconFileId(fileId);
                  setIconPreview(URL.createObjectURL(file));
                }}
              />
            </label>
            {iconFileId ? (
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIconFileId(null);
                  setIconPreview(null);
                }}
              >
                {t('shortcutIcon.remove')}
              </Btn>
            ) : null}
            {upload.progress !== null ? <span className="a-hint">{upload.progress}%</span> : null}
          </div>
          {upload.error ? <div className="a-error">{upload.error}</div> : null}
          <div className="a-hint">{t('shortcutIcon.hint')}</div>
        </div>

        {/*
          De kleur hoort bij de tegel en niet bij het icoon.

          Een snelkoppeling wijst naar een ánder systeem en krijgt dan vaak het generieke
          `link`-icoon; kleur-uit-icoon gaf een rooster van identieke grijze tegels. Met de keuze
          hier kan de kleur iets betekenen. Ze staat nooit alleen - onder elke tegel staat het label.
        */}
        <div className="a-field">
          <div className="a-label" id="shortcut-color-label">
            {t('shortcutsAdmin.color')}
          </div>
          <div className="a-actions" role="radiogroup" aria-labelledby="shortcut-color-label">
            {COLOR_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={color === option}
                aria-label={t(`shortcuts.color.${option}`)}
                title={t(`shortcuts.color.${option}`)}
                className="a-swatch c-pick"
                style={
                  {
                    background: SWATCH[option],
                    '--c-swatch': SWATCH[option],
                  } as CSSProperties
                }
                onClick={() => setColor(option)}
              />
            ))}
          </div>
          <div className="a-hint">{t('beheer.communicatie.shortcuts.colorHint')}</div>
        </div>

        <div className="a-field">
          <div className="a-label" id="shortcut-kind-label">
            {t('beheer.communicatie.shortcuts.kind')}
          </div>
          <Segmented
            labelledBy="shortcut-kind-label"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'fixed', label: t('shortcutsAdmin.kindFixed') },
              { value: 'dynamic', label: t('shortcutsAdmin.kindDynamic') },
            ]}
          />
          <div className="a-hint">{t('beheer.communicatie.shortcuts.kindHint')}</div>
        </div>

        <Field
          label={t('shortcutsAdmin.url')}
          hint={
            kind === 'dynamic'
              ? t('shortcutsAdmin.urlHintDynamic')
              : t('beheer.communicatie.shortcuts.urlHint')
          }
        >
          {(props) => (
            <Input
              {...props}
              className="a-mono"
              value={urlTemplate}
              maxLength={2000}
              placeholder="https://loon.example.com/brief?pnr={personeelsnummer}"
              onChange={(event) => setUrlTemplate(event.target.value)}
            />
          )}
        </Field>

        <AudienceField value={audience} onChange={setAudience} testId="shortcut-audience" />

        <SwitchRow
          title={t('shortcutsAdmin.showOnHome')}
          checked={showOnHome}
          onChange={setShowOnHome}
          testId="shortcut-show-on-home"
        />

        {error ? (
          <Note tone="danger" role="alert">
            {error}
          </Note>
        ) : null}
      </form>
    </Drawer>
  );
}
